import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Wallet, CreditCard, Receipt, BarChart3, RefreshCcw, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MuhasebeHubContent = ({ students, setActiveSection }) => {
  const [syncLoading, setSyncLoading] = useState(false);
  const [accountingSummary, setAccountingSummary] = useState({
    totalFee: 0,
    totalPaid: 0,
    totalPending: 0,
    studentCount: 0
  });

  // Muhasebe özeti hesapla
  useEffect(() => {
    const studentsWithFee = students.filter(s => s.role === 'student' && s.total_fee > 0);
    const totalFee = studentsWithFee.reduce((sum, s) => sum + (s.total_fee || 0), 0);
    const totalPaid = studentsWithFee.reduce((sum, s) => sum + (s.paid_amount || 0), 0);
    setAccountingSummary({
      totalFee,
      totalPaid,
      totalPending: totalFee - totalPaid,
      studentCount: studentsWithFee.length
    });
  }, [students]);

  const handleSyncPayments = async () => {
    setSyncLoading(true);
    try {
      const response = await axios.post(`${API}/admin/payments/sync-from-students`, {}, { withCredentials: true });
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Senkronizasyon başarısız');
    } finally {
      setSyncLoading(false);
    }
  };

  const sections = [
    { id: 'accounting-payments', title: 'Ödeme Yönetimi', description: 'Öğrenci ödemeleri ve tahsilatlar', icon: <CreditCard className="w-6 h-6" />, stats: 'Ödemeler', color: 'emerald' },
    { id: 'accounting-invoices', title: 'Fatura Yönetimi', description: 'Fatura oluştur ve takip et', icon: <Receipt className="w-6 h-6" />, stats: 'Faturalar', color: 'blue' },
    { id: 'accounting-reports', title: 'Mali Raporlar', description: 'Gelir-gider raporları ve analizler', icon: <BarChart3 className="w-6 h-6" />, stats: 'Raporlar', color: 'purple' }
  ];

  const colorClasses = {
    emerald: 'bg-emerald-100 text-emerald-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-600 rounded-lg text-white">
            <Wallet className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Muhasebe</h2>
            <p className="text-sm text-gray-500">Finansal işlemler ve raporlar</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleSyncPayments}
          disabled={syncLoading}
          className="gap-2"
        >
          <RefreshCcw className={`w-4 h-4 ${syncLoading ? 'animate-spin' : ''}`} />
          Öğrenci Ücretlerini Aktar
        </Button>
      </div>

      {/* Muhasebe Özet Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4">
            <p className="text-blue-100 text-sm">Toplam Alacak</p>
            <p className="text-2xl font-bold">{accountingSummary.totalFee.toLocaleString('tr-TR')} ₺</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="p-4">
            <p className="text-green-100 text-sm">Tahsil Edilen</p>
            <p className="text-2xl font-bold">{accountingSummary.totalPaid.toLocaleString('tr-TR')} ₺</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="p-4">
            <p className="text-orange-100 text-sm">Bekleyen</p>
            <p className="text-2xl font-bold">{accountingSummary.totalPending.toLocaleString('tr-TR')} ₺</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="p-4">
            <p className="text-purple-100 text-sm">Kayıtlı Öğrenci</p>
            <p className="text-2xl font-bold">{accountingSummary.studentCount}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sections.map(section => (
          <Card 
            key={section.id}
            className="border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer group bg-white"
            onClick={() => setActiveSection(section.id)}
          >
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${colorClasses[section.color]}`}>
                  {section.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-800 group-hover:text-emerald-600 transition-colors">{section.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{section.description}</p>
                  <span className="inline-block mt-2 text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">{section.stats}</span>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default MuhasebeHubContent;
